import { createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { WindowsState, WindowPosition } from '@/types/store';
/* eslint-enable @typescript-eslint/no-unused-vars */
import { v4 as uuidv4 } from 'uuid';

const initialState: WindowsState = {
  windows: {},
  activeWindowId: null,
  nextZIndex: 100,
  layouts: {
    default: {
      name: 'Default',
      description: 'Default window layout',
      positions: {}
    }
  },
  activeLayout: 'default',
  recentlyClosedWindows: [],
  maxRecentlyClosed: 5,
};

interface CreateWindowPayload {
  title: string;
  position?: Partial<WindowPosition>;
  content?: string;
  type: 'settings' | 'editor' | 'results' | 'help' | 'custom';
}

interface UpdateWindowPositionPayload {
  id: string;
  position: Partial<WindowPosition>;
}

interface SaveLayoutPayload {
  name: string;
  description?: string;
}

interface ArrangeWindowsPayload {
  arrangement: 'cascade' | 'tile' | 'horizontal' | 'vertical';
}

// Helper function to determine window position based on arrangement
const getArrangedPositions = (
  windows: Record<string, any>, 
  arrangement: 'cascade' | 'tile' | 'horizontal' | 'vertical'
) => {
  const windowIds = Object.keys(windows);
  const count = windowIds.length;
  
  if (count === 0) return {};
  
  const positions: Record<string, WindowPosition> = {};
  
  // Get the viewport dimensions (in a real application, we would use window.innerWidth/Height)
  const viewportWidth = 1200;
  const viewportHeight = 800;

  if (arrangement === 'cascade') {
    const offsetX = 50;
    const offsetY = 40;
    
    windowIds.forEach((id, index) => {
      positions[id] = {
        x: Math.min(offsetX * index, viewportWidth / 2),
        y: Math.min(offsetY * index, viewportHeight / 2),
        width: 600,
        height: 400,
        zIndex: 100 + index
      };
    });
  } else if (arrangement === 'tile') {
    // Calculate rows and columns for a grid
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    
    const tileWidth = viewportWidth / cols;
    const tileHeight = viewportHeight / rows;
    
    windowIds.forEach((id, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      
      positions[id] = {
        x: col * tileWidth,
        y: row * tileHeight,
        width: tileWidth,
        height: tileHeight,
        zIndex: 100 + index
      };
    });
  } else if (arrangement === 'horizontal') {
    const height = viewportHeight;
    const width = viewportWidth / count;
    
    windowIds.forEach((id, index) => {
      positions[id] = {
        x: index * width,
        y: 0,
        width,
        height,
        zIndex: 100 + index
      };
    });
  } else if (arrangement === 'vertical') {
    const height = viewportHeight / count;
    const width = viewportWidth;
    
    windowIds.forEach((id, index) => {
      positions[id] = {
        x: 0,
        y: index * height,
        width,
        height,
        zIndex: 100 + index
      };
    });
  }
  
  return positions;
};

const windowsSlice = createSlice({
  name: 'windows',
  initialState,
  reducers: {
    createWindow: (state, action: PayloadAction<CreateWindowPayload>) => {
      const id = uuidv4();
      const { title, position, content, type } = action.payload;

      const defaultPosition = {
        x: 100,
        y: 100,
        width: 600,
        height: 400,
        zIndex: state.nextZIndex,
      };

      state.windows[id] = {
        id,
        title,
        position: {
          ...defaultPosition,
          ...position,
        },
        isOpen: true,
        isMinimized: false,
        isMaximized: false,
        content: content || '',
        type,
      };

      state.activeWindowId = id;
      state.nextZIndex += 1;
    },

    closeWindow: (state, action: PayloadAction<string>) => {
      if (state.windows[action.payload]) {
        // Store in recently closed if not already at limit
        if (state.recentlyClosedWindows.length < state.maxRecentlyClosed) {
          state.recentlyClosedWindows.push({ ...state.windows[action.payload] });
        } else {
          // Remove oldest and add new one
          state.recentlyClosedWindows.shift();
          state.recentlyClosedWindows.push({ ...state.windows[action.payload] });
        }
        
        state.windows[action.payload].isOpen = false;

        // If the closed window was active, set activeWindowId to null
        if (state.activeWindowId === action.payload) {
          state.activeWindowId = null;
        }
      }
    },

    minimizeWindow: (state, action: PayloadAction<string>) => {
      if (state.windows[action.payload]) {
        state.windows[action.payload].isMinimized = true;

        // If the minimized window was active, set activeWindowId to null
        if (state.activeWindowId === action.payload) {
          state.activeWindowId = null;
        }
      }
    },

    maximizeWindow: (state, action: PayloadAction<string>) => {
      if (state.windows[action.payload]) {
        state.windows[action.payload].isMaximized = true;
        state.activeWindowId = action.payload;
        state.windows[action.payload].position.zIndex = state.nextZIndex;
        state.nextZIndex += 1;
      }
    },

    restoreWindow: (state, action: PayloadAction<string>) => {
      if (state.windows[action.payload]) {
        state.windows[action.payload].isMinimized = false;
        state.windows[action.payload].isMaximized = false;
        state.activeWindowId = action.payload;
        state.windows[action.payload].position.zIndex = state.nextZIndex;
        state.nextZIndex += 1;
      }
    },

    focusWindow: (state, action: PayloadAction<string>) => {
      if (state.windows[action.payload] && state.windows[action.payload].isOpen) {
        state.activeWindowId = action.payload;
        state.windows[action.payload].position.zIndex = state.nextZIndex;
        state.nextZIndex += 1;
      }
    },

    updateWindowPosition: (state, action: PayloadAction<UpdateWindowPositionPayload>) => {
      const { id, position } = action.payload;

      if (state.windows[id]) {
        state.windows[id].position = {
          ...state.windows[id].position,
          ...position,
        };
      }
    },

    updateWindowContent: (state, action: PayloadAction<{ id: string; content: string }>) => {
      const { id, content } = action.payload;

      if (state.windows[id]) {
        state.windows[id].content = content;
      }
    },

    deleteWindow: (state, action: PayloadAction<string>) => {
      if (state.windows[action.payload]) {
        delete state.windows[action.payload];

        // If the deleted window was active, set activeWindowId to null
        if (state.activeWindowId === action.payload) {
          state.activeWindowId = null;
        }
      }
    },

    clearAllWindows: state => {
      state.windows = {};
      state.activeWindowId = null;
      state.nextZIndex = 100;
    },
    
    reopenLastClosedWindow: state => {
      if (state.recentlyClosedWindows.length > 0) {
        const lastWindow = state.recentlyClosedWindows.pop();
        if (lastWindow) {
          const id = uuidv4();
          lastWindow.id = id;
          lastWindow.isOpen = true;
          lastWindow.position.zIndex = state.nextZIndex;
          state.windows[id] = lastWindow;
          state.activeWindowId = id;
          state.nextZIndex += 1;
        }
      }
    },
    
    saveLayout: (state, action: PayloadAction<SaveLayoutPayload>) => {
      const { name, description } = action.payload;
      const layoutId = name.toLowerCase().replace(/\s+/g, '-');
      
      // Create positions map for all windows
      const positions: Record<string, WindowPosition> = {};
      Object.keys(state.windows).forEach(id => {
        if (state.windows[id].isOpen) {
          positions[id] = { ...state.windows[id].position };
        }
      });
      
      state.layouts[layoutId] = {
        name,
        description: description || `Layout created at ${new Date().toLocaleString()}`,
        positions
      };
      
      state.activeLayout = layoutId;
    },
    
    applyLayout: (state, action: PayloadAction<string>) => {
      const layoutId = action.payload;
      
      if (state.layouts[layoutId]) {
        const layout = state.layouts[layoutId];
        
        // Apply positions from layout to windows
        Object.keys(layout.positions).forEach(id => {
          if (state.windows[id]) {
            state.windows[id].position = { ...layout.positions[id] };
          }
        });
        
        state.activeLayout = layoutId;
      }
    },
    
    deleteLayout: (state, action: PayloadAction<string>) => {
      const layoutId = action.payload;
      
      if (layoutId !== 'default' && state.layouts[layoutId]) {
        delete state.layouts[layoutId];
        
        if (state.activeLayout === layoutId) {
          state.activeLayout = 'default';
        }
      }
    },
    
    arrangeWindows: (state, action: PayloadAction<ArrangeWindowsPayload>) => {
      const { arrangement } = action.payload;
      
      // Get only open windows
      const openWindows: Record<string, any> = {};
      Object.keys(state.windows).forEach(id => {
        if (state.windows[id].isOpen && !state.windows[id].isMinimized) {
          openWindows[id] = state.windows[id];
        }
      });
      
      const positions = getArrangedPositions(openWindows, arrangement);
      
      // Apply the new positions
      Object.keys(positions).forEach(id => {
        if (state.windows[id]) {
          state.windows[id].position = positions[id];
        }
      });
    },
  },
});

export const {
  createWindow,
  closeWindow,
  minimizeWindow,
  maximizeWindow,
  restoreWindow,
  focusWindow,
  updateWindowPosition,
  updateWindowContent,
  deleteWindow,
  clearAllWindows,
  reopenLastClosedWindow,
  saveLayout,
  applyLayout,
  deleteLayout,
  arrangeWindows,
} = windowsSlice.actions;

export default windowsSlice.reducer;
